import { Loading01Icon, XIcon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import SaveConfirmationModal from '@/components/SaveConfirmationModal';
import { Button, IconButton, Text, Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';

import { PlaygroundInputSourceType } from '../PlaygroundContext';
import {
  isDatasetExamplesEdited,
  usePlaygroundStore,
} from './hooks/usePlaygroundStore';
import { useSaveDataset } from './hooks/useSaveDataset';

export const EvaluateModeButton = () => {
  const inputSourceType = usePlaygroundStore((state) => state.inputSourceType);
  const setInputSourceType = usePlaygroundStore(
    (state) => state.setInputSourceTypeAndUpdateUrl
  );
  const setDatasetId = usePlaygroundStore(
    (state) => state.setDatasetIdAndUpdateUrl
  );
  const setIsCreatingNewDataset = usePlaygroundStore(
    (state) => state.setIsCreatingNewDataset
  );
  const onCreateNewDataset = usePlaygroundStore(
    (state) => state.onCreateNewDataset
  );
  const examples = usePlaygroundStore((state) => state.examples);
  const isDatasetEdited = isDatasetExamplesEdited(examples);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const switchToManual = () => {
    setInputSourceType(PlaygroundInputSourceType.MANUAL);
    setDatasetId(undefined);
    setIsCreatingNewDataset(false);
  };

  const { saveDataset, isLoading: isLoadingDataset } = useSaveDataset({
    onSuccess: switchToManual,
  });

  const onClose = () => setIsConfirmationModalOpen(false);

  return (
    <>
      {inputSourceType === PlaygroundInputSourceType.DATASET ? (
        <div
          className={cn(
            'flex h-[26px] items-center gap-0.5  px-2',
            'whitespace-nowrap',
            'rounded-sm border border-brand bg-brand-secondary'
          )}
        >
          <Text variant="sm" weight="normal">
            Evaluate Mode
          </Text>
          <IconButton
            icon={XIcon}
            label="Exit Evaluation Mode"
            onClick={() => {
              if (isDatasetEdited) {
                setIsConfirmationModalOpen(true);
              } else {
                switchToManual();
              }
            }}
            className="bg-transparent p-0"
            color="secondary"
            variant="plain"
            tooltipProps={{
              side: 'bottom',
              title: (
                <div className="flex flex-col gap-1">
                  <Text variant="sm" weight="medium">
                    Exit Evaluation Mode
                  </Text>
                  <Text variant="xs" weight="normal">
                    This will remove the dataset you're currently evaluating and
                    return to manual input mode
                  </Text>
                </div>
              ),
            }}
          />
        </div>
      ) : (
        <Tooltip
          title="Measure performance with evaluations"
          description="Test your prompt over a dataset and set up evaluators to measure how well your application is performing"
        >
          <Button
            onClick={() => {
              onCreateNewDataset(true);
            }}
            color="secondary"
            variant="outlined"
          >
            Set up Evaluation
          </Button>
        </Tooltip>
      )}
      <SaveConfirmationModal
        isOpen={isConfirmationModalOpen}
        title="Exit Evaluation Mode?"
        description={`You have unsaved changes to the dataset. Do you want to save them before exiting Experiment Mode?`}
        onClose={onClose}
        onConfirm={saveDataset}
        onDiscard={() => {
          switchToManual();
          onClose();
        }}
        saveCopy={
          isLoadingDataset ? (
            <Loading01Icon className="h-4 w-4 animate-spin" />
          ) : (
            'Save and Exit'
          )
        }
        discardCopy="Discard Changes"
      />
    </>
  );
};
